import Axios from 'axios'
import router from './../../router'

async function selectCube({ state, dispatch }, data) {
  const cubeName = typeof data === 'string' ? data : data.cubeName
  const reloadApp = typeof data === 'string' ? true : data.reloadApp

  try {
    const result = await _updateSessionSettings(state, {
      selectedCubeName: cubeName,
    })
    if (result && result.data.bSuccess && reloadApp) {
      // Fully reload the app with the newly selected cube.
      window.location.href = router.currentRoute.query.redirect || '/'
    }
  } catch (err) {
    dispatch('logError', 'Error selecting cube: ' + err)
  }
}

async function _updateSessionSettings(state, settings) {
  const token = state.authentication.backendAccessToken
  const url = `${state.urls.backend}/app/SessionSettings/${token}`
  const config = {
    headers: {
      efToken: state.authentication.backendAccessToken,
    },
  }
  return await Axios.put(url, settings, config)
}

async function updateSessionSettings({ dispatch, state }, settings) {
  try {
    return await _updateSessionSettings(state, settings)
  } catch (err) {
    dispatch('logError', 'Error updating session settings: ' + err)
  }
}

async function initializeApp({ commit, state, dispatch, getters }) {
  if (!state.authentication.backendAccessToken) {
    return false
  }

  try {
    let getParameters = {
      requestCurrentRead: true,
    }

    const url = state.urls.backend + '/app/getAppConfig'
    const result = await Axios.get(url, {
      params: getParameters,
      headers: {
        efToken: state.authentication.backendAccessToken,
      },
    })

    if (!result.data.data.cubeSelected) {
      // If no cube was selected for this session, the available cubes are in
      // result.data.data.accessObjects. But if one was selected, they are in
      // result.data.data.cubeAuthData.accessObjects,
      commit('availableCubesReceived', result.data.data.accessObjects)

      if (getters.availableCubes.length === 1) {
        // There is only a single cube, let's select it automatically.
        // Note that the select cube action reloads the app.
        dispatch('selectCube', getters.availableCubes[0].cubeName)
      } else if (router.currentRoute.name !== 'select-cube') {
        // Show the select cube page, if there are multiple available cubes.
        router.push({
          name: 'select-cube',
        })
      }
    } else {
      // The user has selected a cube already.
      // Note that in this case the available cubes are location in a different
      // position than if no cube was selected.
      commit(
        'availableCubesReceived',
        result.data.data.cubeAuthData.accessObjects
      )

      commit('appInitialized', result.data)

      // Notifications popup.
      // We need to make sure the appInitialized mutation was called and the
      // serviceworker was registered in order to show the popup requesting
      // permission for notifications.
      if (getters.serviceWorkerRegistered) {
        dispatch('setupPushNotifications')
      }

      commit('meterDataReceived', result.data.data.currentRead)
      commit('setIsLoading', false)

      dispatch('setupMeterDataConnection')

      // We retrieve the connections this cube has. These are different for each user.
      dispatch('updateCubeConnections')

      // Note that we only load the google maps js if we did not already. So if a
      // user logs in, the map is loaded and displayed, he logs out and logs in
      // with a different user. The maps js is not re-loaded but reused.
      if (!state.global.googleMapsJavascriptLoaded) {
        dispatch('loadGoogleMaps')
      }

      // Even though the global list of cubes is - well - global, we need to fetch
      // it everytime because we preprocess each cube which is dependend on the
      // current user and cube.
      dispatch('loadGlobalCubesList')

      // Fetch the prices of this user's consumers. We do this because reem's db
      // is guaranteed to have the correct + current prices. This way we avoid any
      // possible inconsistencies.
      dispatch('getConsumerPrices')

      // Load the user's contract data and the default selling price from reem.
      dispatch('loadContractData')
      dispatch('getDefaultSellingPrice')

      // load downloadables
      dispatch('loadDownloadables')

      // Fetch history data for the chart.
      dispatch('loadHistoryData')

      dispatch('getAllDevices')

      // Set up production types
      commit('setProductionTypes', result.data.data.productionTypes)

      // set Service supplier data
      commit(
        'setSupplierData',
        result.data.data.customerInfo.supplierServiceAgreement
      )

      // Redirect to original URL if it exists
    }
  } catch (err) {
    dispatch('logError', 'The intializeApp request failed: ' + err)
    commit('userLoggedOut')
  }
}

async function setupPushNotifications({ dispatch, state, getters, commit }) {
  if (!getters.browserSupportsNotifications) {
    return
  }

  // TODO Handle empty object as result should return false.
  const webPushSubscription = await getters.getBrowserWebPushSubscription
  const hasNotificationsPermission = await getters.hasNotificationsPermission
  const userHasSubscribed = getters.pushNotificationsEnabled

  if (userHasSubscribed && hasNotificationsPermission && webPushSubscription) {
    // Everything seems to be set up already, nothing to do.
    return
  }

  if (userHasSubscribed) {
    // The user has enabled subscription in another app instance/browser/device,
    // but something is not set up yet.

    // Since at least some browsers only allow subscribing to web push if we
    // have the notifications permission, we need to show the request permissions
    // popup if we don't have it.
    if (hasNotificationsPermission) {
      dispatch('subscribeToPushApi')
    } else {
      commit('showNotificationsRequestPopup', true)
    }
  }
  // The user did not decide whether or not to enable notifications. Let's ask.
  else if (
    state.cube.webPushValue === 0 ||
    state.cube.notificationsValue === 0
  ) {
    commit('showNotificationsRequestPopup', true)
  }
}

function logError({ state }, data) {
  // Normalize / prepare the data to log.
  if (typeof data === 'string') {
    data = {
      message: data,
    }
  }
  if (!data.logLevel) {
    data.logLevel = 'error'
  }

  if (!data.timestamp) {
    data.timestamp = new Date()
  }

  if (state.global.environment.indexOf('dev') >= 0) {
    console.log(data)
  }

  try {
    const url = state.urls.backend + '/app/log'

    Axios.post(
      url,
      {
        appType: 'appv2-' + state.global.environment,
        logLevel: data.logLevel,
        cubeName: state.global.cubeName,
        message: data.message,
        timestamp: data.timestamp,
        data: data.data ? JSON.stringify(data.data) : null,
      },
      {
        headers: {
          efToken: state.authentication.backendAccessToken,
        },
      }
    )
  } catch (err) {
    if (typeof window.console === 'function') {
      console.log('Could not remotely log error: ' + data.message)
    }
  }
}

function loadGoogleMaps({ commit, state }) {
  commit('setGoogleMapsStatus', 'loading')
  const script = document.createElement('script')
  script.type = 'text/javascript'
  script.src =
    state.global.googleMapsJavascriptBaseUrl +
    '?key=' +
    state.global.googleMapsApiKey
  script.addEventListener(
    'load',
    () => {
      commit('setGoogleMapsStatus', 'loaded')
    },
    false
  )
  document.body.appendChild(script)
}

async function notificationsPermissionRequested(
  { state, commit, dispatch, getters },
  permission
) {
  // Make sure, older chrome versions store the permission result.
  if (!('permission' in Notification)) {
    Notification.permission = permission
  }

  if (permission !== 'granted') {
    return
  }

  try {
    const url = state.urls.backend + '/app/notifications/subscribe'
    const result = await Axios.post(
      url,
      {},
      {
        headers: {
          efToken: state.authentication.backendAccessToken,
        },
      }
    )

    if (
      !result ||
      result.status.toString().substr(0, 1) !== '2' ||
      !result.data.bSuccess
    ) {
      throw new Error(
        `Error updating notification subscription on server: ${result.data.Message}.`
      )
    }

    commit('enableNotifications', true)
    dispatch('showNotification', {
      title: 'eFriends',
      body: getters.str('notifications.welcomeNotification'),
      icon: `${getters.assetsPath}/logo.svg`,
    })
  } catch (e) {
    dispatch('logError', 'Error requesting notifications permission', e)
  }
}

async function disableNotifications({ state, commit, dispatch }) {
  try {
    const url = state.urls.backend + '/app/notifications/unsubscribe'
    const result = await Axios.post(
      url,
      {},
      {
        headers: {
          efToken: state.authentication.backendAccessToken,
        },
      }
    )

    if (
      !result ||
      result.status.toString().substr(0, 1) !== '2' ||
      !result.data.bSuccess
    ) {
      throw new Error(
        `Error disablig notification subscription on server: ${result.data.Message}.`
      )
    }
    commit('enableNotifications', false)
  } catch (e) {
    dispatch('logError', 'Error disabling notifications', e)
  }
}

async function subscribeToPushApi({ commit, state, getters }) {
  try {
    const swRegistration = state.global.serviceWorkerRegistration
    if (!swRegistration) {
      throw new Error('Serviceworker not registered yet.')
    }

    let subscription = await getters.getBrowserWebPushSubscription
    // TODO Handle case when the subscription object is an empty object.
    if (
      !subscription ||
      (Object.keys(subscription).length === 0 &&
        subscription.constructor === Object)
    ) {
      // Subscribe if we're not subscribed yet.
      subscription = await swRegistration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: process.env.VUE_APP_VAPID_PUBLIC_KEY,
      })
    }

    // The subscription object only contains all needed data when stringifying
    // it. At least I did not find another way to do this.
    const subscriptionData = JSON.parse(JSON.stringify(subscription))

    const url = state.urls.backend + '/app/push/subscribe'
    const result = await Axios.post(url, subscriptionData, {
      headers: {
        efToken: state.authentication.backendAccessToken,
      },
    })

    if (
      !result ||
      result.status.toString().substr(0, 1) !== '2' ||
      !result.data.bSuccess
    ) {
      throw new Error(
        `Error updating push subscription on server: ${result.data.Message}.`
      )
    }

    commit('setPushSubscription', subscription)
  } catch (err) {
    throw new Error(
      'Error adding push subscription to notification service: ' + err
    )
  }
}

async function unsubscribeFromPushApi({ state, commit }) {
  try {
    const url = state.urls.backend + '/app/push/unsubscribe'
    const result = await Axios.post(
      url,
      {},
      {
        headers: {
          efToken: state.authentication.backendAccessToken,
        },
      }
    )

    if (
      !result ||
      result.status.toString().substr(0, 1) !== '2' ||
      !result.data.bSuccess
    ) {
      throw new Error(
        `Error unsubscribing from push on server: ${result.data.Message}.`
      )
    }

    commit('setPushSubscription', false)
  } catch (err) {
    throw new Error(
      'Error removing push subscription to notification service: ' + err
    )
  }
}

async function markNotificationsAsRead({ state, commit }, data) {
  // Default to marking as read, but allow setting false too.
  data.isRead = typeof data.isRead !== 'undefined' ? data.isRead : true
  let url = state.urls.backend

  // Note that we're committing the changes first, since we want to immediately
  // show changes. No need to wait for a http request to mark notifications.
  // Even if it fails it's ok.
  commit('markNotificationsAsRead', data)

  if (data.notification) {
    url += `/app/notifications/Notification/${data.notification.id}`
  } else if (data.fromCubeName) {
    url += `/app/notifications/TriggeringCube/${data.fromCubeName}/${state.cube.name}`
  } else if (data.all) {
    url += `/app/notifications/Cube/${state.cube.name}`
  }

  try {
    const result = await Axios.put(
      url,
      {
        read: data.isRead,
      },
      {
        headers: {
          efToken: state.authentication.backendAccessToken,
        },
      }
    )

    if (
      !result ||
      result.status.toString().substr(0, 1) !== '2' ||
      !result.data.bSuccess
    ) {
      throw new Error(result.data.Message)
    }
  } catch (err) {
    throw new Error(`Error marking notifications as read: ${err}.`)
  }
}

async function requestNotificationsPermission({ dispatch, commit }) {
  // Safari on ios still uses the old callback-based implementation of notifications.
  try {
    // Request and store the notification sound as soon as the user clicked yes
    // on our notifications popup, so the sound can be played when showing the
    // test notification.
    const notificationSound = new Audio('/notification.mp3')
    commit('setNotificationSound', notificationSound)

    Notification.requestPermission().then((permission) => {
      dispatch('notificationsPermissionRequested', permission)
    })
  } catch (e) {
    Notification.requestPermission((permission) => {
      dispatch('notificationsPermissionRequested', permission)
    })
  }
}

function showNotification({ getters }, data) {
  const sound = getters.notificationSound
  if (sound) {
    sound.play()
  }
  return new Notification(data.title, data)
}

export default {
  selectCube,
  initializeApp,
  logError,
  loadGoogleMaps,
  subscribeToPushApi,
  unsubscribeFromPushApi,
  requestNotificationsPermission,
  notificationsPermissionRequested,
  disableNotifications,
  updateSessionSettings,
  markNotificationsAsRead,
  setupPushNotifications,
  showNotification,
}
